/**
 * Schema fragments shared by the API and the browser.
 *
 * TypeBox gives one definition two jobs: Ajv validates the request at runtime,
 * and TypeScript infers the handler's parameter types from the same object. A
 * field cannot drift between the validator and the type any more.
 */
import { Type, type Static, type TSchema } from '@sinclair/typebox';
import { MAX_KEYWORD_LENGTH, MAX_KEYWORDS } from '../keywords.ts';

export const Uuid = Type.String({ format: 'uuid' });

export const Text = (maxLength: number, minLength = 1) =>
  Type.String({ minLength, maxLength });

export const OptionalText = (maxLength: number) => Type.String({ minLength: 0, maxLength });

/**
 * A theme's seed colour, `#rrggbb`, or null for "inherit". The value reaches
 * CSS, so nothing but a six-digit hex colour is admitted - no named colours, no
 * functions, nothing a stylesheet could be made to read as anything else.
 */
export const BrandColor = Type.Union([
  Type.Null(),
  Type.String({ pattern: '^#[0-9a-fA-F]{6}$' }),
]);

/** HTTPS only. No credentials in the authority, no other scheme. */
export const HttpsUrl = Type.String({
  format: 'uri',
  maxLength: 2000,
  pattern: '^https://[^\\s@]+$',
});

export const Email = Type.String({ format: 'email', maxLength: 320 });

export const EntityStatus = Type.Union([
  Type.Literal('ACTIVE'),
  Type.Literal('SUSPENDED'),
  Type.Literal('ARCHIVED'),
]);

/**
 * A contract's state, which is not the clinic's own status.
 *
 * A clinic can be SUSPENDED (it may not sign in) while its subscription is
 * ACTIVE, and it can be signed in and working while the contract is PAUSED and
 * refusing to reserve quota. Two questions, two columns; conflating them is how
 * "why can they log in but not generate" becomes unanswerable.
 *
 * PAUSED keeps everything and grants nothing. CANCELED is the end of a contract
 * and steps out of the way of a replacement - see migration 071.
 */
export const SubscriptionStatus = Type.Union([
  Type.Literal('ACTIVE'),
  Type.Literal('PAUSED'),
  Type.Literal('CANCELED'),
]);

/**
 * A membership's state, which is its own enum and not `entity_status`.
 *
 * `member_status` also carries INVITED, left over from the invitation flow
 * dropped in migration 009. Nothing can reach that state any more, so it is not
 * offered here - a membership is either usable or it is not.
 */
export const MemberStatus = Type.Union([Type.Literal('ACTIVE'), Type.Literal('DISABLED')]);

/**
 * What a `strengths` row describes. One table carries all five because they are
 * the same shape - a title, a body, a scope and an approval state - and five
 * tables would mean five sets of CRUD drifting apart.
 *
 * OTHER is the one that is not a kind of thing but the absence of one: material
 * the clinic wants an article to be able to use and that none of the four
 * describes - aftercare wording, a parking note, how a cartridge depth is
 * chosen. It briefly had a table of its own (migrations 081-084); it did not
 * need one, because a note is a title, a body and a priority, which is what a
 * strength already was.
 */
export const FactCategory = Type.Union([
  Type.Literal('STRENGTH'),
  Type.Literal('TREATMENT_POLICY'),
  Type.Literal('COUNSELING_POLICY'),
  Type.Literal('EQUIPMENT'),
  Type.Literal('OTHER'),
]);
export type FactCategory = Static<typeof FactCategory>;

/**
 * What a row of 施術メニュー actually is.
 *
 * The table was modelled on one procedure with a price, a downtime and a risk
 * profile - and a clinic's price page is not that. It carries courses, monthly
 * plans, monitor rates and consultation fees, none of which has a downtime to
 * list. Storing them was never the problem; nothing SAID what a row was, so the
 * form asked a 月額プラン for its risks, the harvester went looking only for
 * 施術, and the prompt described a 5回コース as a treatment.
 *
 * An enum because three consumers branch on it - the form hides what does not
 * apply, the extraction model is told what to look for, and the prompt
 * introduces the row by its kind. See migration 085.
 */
export const TreatmentKind = Type.Union([
  Type.Literal('TREATMENT'),
  Type.Literal('COURSE'),
  Type.Literal('PLAN'),
  Type.Literal('OFFER'),
  Type.Literal('FEE'),
  Type.Literal('OTHER'),
]);
export type TreatmentKind = Static<typeof TreatmentKind>;

/**
 * A keyword list, on every route that takes one.
 *
 * `uniqueItems` is the schema saying what shared/keywords.ts says: the same
 * keyword twice is not a list of two. Rejected rather than silently collapsed,
 * because a client that sends duplicates has a bug the writer cannot see, and
 * every client of this API normalises before it posts.
 */
export const Keywords = Type.Array(Type.String({ minLength: 1, maxLength: MAX_KEYWORD_LENGTH }), {
  maxItems: MAX_KEYWORDS,
  uniqueItems: true,
});

/**
 * A store's keyword override, which has three states rather than two.
 *
 * An absent field leaves the override alone, `null` removes it so the store
 * inherits the clinic's list again, and an array replaces it - including an
 * empty array, which is a store that deliberately carries no keywords. Losing
 * that third state is how "inherit" and "none" become the same button.
 */
export const KeywordsOverride = Type.Union([Type.Null(), Keywords]);

/**
 * The H2 headings a body will be written to, in order.
 *
 * Longer than a keyword and fewer of them: a heading is a sentence fragment, and
 * twenty is past the point where a 5,000-character article can serve them all.
 * Not `uniqueItems` - two sections legitimately share a heading in a long
 * article ("よくある質問" under two treatments), and the writer is choosing these
 * by hand.
 */
export const Headings = Type.Array(Type.String({ minLength: 1, maxLength: 120 }), {
  maxItems: 20,
});

/**
 * Where a store is, when it is open, and how to book it.
 *
 * One definition for the four routes that write it (a clinic admin's store
 * screen and the platform's, each of which can create and update) so the field
 * a screen offers and the field a route accepts cannot drift. The keys are the
 * column names in camelCase; `server/lib/patch.ts` maps them back.
 *
 * Text fields are NOT NULL in the database, so their absence means "unchanged"
 * and an empty string means "cleared". The two URLs are nullable, so they also
 * accept an explicit null - an absent link and an empty one are different to a
 * reader who clicks it.
 */
export const STORE_PROFILE_FIELDS = {
  postalCode: Type.Optional(OptionalText(16)),
  /*
   * Registered, never parsed out of `address`. 「京都府京都市」 and 「東京都府中市」
   * both defeat the obvious split rule, and a region that is right most of the
   * time is worse than an empty one because nobody checks it. Local SEO turns on
   * these two, so they are keywords rather than decoration - see migration 039.
   */
  prefecture: Type.Optional(OptionalText(20)),
  city: Type.Optional(OptionalText(60)),
  address: Type.Optional(OptionalText(300)),
  accessNote: Type.Optional(OptionalText(500)),
  phone: Type.Optional(OptionalText(40)),
  hoursNote: Type.Optional(OptionalText(500)),
  closedNote: Type.Optional(OptionalText(200)),
  reservationUrl: Type.Optional(Type.Union([Type.Null(), HttpsUrl])),
  mapUrl: Type.Optional(Type.Union([Type.Null(), HttpsUrl])),
};

/**
 * The operating entity behind the brand.
 *
 * `corporate_name` is not `clinics.name`: 「医療法人◯◯会」operates
 * 「◯◯美容クリニック」, and an article's footer needs the former while its
 * text uses the latter. `adManagerName` is 広告管理責任者, whom Japan's
 * 医療広告ガイドライン expects a publishing clinic to name.
 */
export const CLINIC_PROFILE_FIELDS = {
  corporateName: Type.Optional(OptionalText(200)),
  representative: Type.Optional(OptionalText(100)),
  /** See STORE_PROFILE_FIELDS: registered, not derived. */
  prefecture: Type.Optional(OptionalText(20)),
  city: Type.Optional(OptionalText(60)),
  address: Type.Optional(OptionalText(300)),
  phone: Type.Optional(OptionalText(40)),
  adManagerName: Type.Optional(OptionalText(100)),
};

export const STORE_PROFILE_KEYS = Object.keys(STORE_PROFILE_FIELDS);
export const CLINIC_PROFILE_KEYS = Object.keys(CLINIC_PROFILE_FIELDS);

/** The same columns, for the SELECT lists that hand them back. */
export const STORE_PROFILE_COLUMNS =
  'postal_code, prefecture, city, address, access_note, phone, hours_note, closed_note,'
  + ' reservation_url, map_url';
export const CLINIC_PROFILE_COLUMNS =
  'corporate_name, representative, prefecture, city, address, phone, ad_manager_name';

export const FactStatus = Type.Union([
  Type.Literal('DRAFT'),
  Type.Literal('APPROVED'),
  Type.Literal('RETIRED'),
]);

/** Every list endpoint uses this. Pagination ceilings are not optional. */
const PAGINATION_FIELDS = {
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100, default: 25 })),
  offset: Type.Optional(Type.Integer({ minimum: 0, maximum: 100_000, default: 0 })),
};

export const Pagination = Type.Object(PAGINATION_FIELDS, { additionalProperties: false });
export type Pagination = Static<typeof Pagination>;

/**
 * Pagination plus this endpoint's filters, as ONE object.
 *
 * Do not reach for Type.Intersect here: each branch keeps its own
 * `additionalProperties: false`, so Pagination alone would reject every filter
 * the other branch defines and the request 400s with a confusing message.
 */
export const PaginatedQuery = <T extends Record<string, TSchema>>(properties: T) =>
  Type.Object({ ...PAGINATION_FIELDS, ...properties }, { additionalProperties: false });

/** Unknown keys are a mass-assignment vector: reject the request, don't strip. */
export const Body = <T extends Record<string, TSchema>>(properties: T) =>
  Type.Object(properties, { additionalProperties: false });

export const IdParam = Type.Object({ id: Uuid }, { additionalProperties: false });

export const ErrorResponse = Type.Object({
  error: Type.Object({
    code: Type.String(),
    message: Type.String(),
    requestId: Type.String(),
  }),
});
export type ErrorResponse = Static<typeof ErrorResponse>;

/** Roles a clinic administrator is allowed to hand out. Platform roles absent by design. */
/**
 * The three tenant roles, widest first. JMC_ADMIN is a platform role and is
 * deliberately not here: no tenant screen can mint one.
 */
export const TenantRole = Type.Union([
  Type.Literal('CLINIC_ADMIN'),
  Type.Literal('STORE_ADMIN'),
  Type.Literal('STORE_USER'),
]);
export type TenantRole = Static<typeof TenantRole>;
